/*
 * Cart-drawer upsell orchestration (home-grown UpCart replacement).
 *
 * The rewards bar, announcement, and trust badges are pure Liquid re-rendered by the Sections
 * Rendering API on every cart mutation, so this file only handles what genuinely needs JS:
 *
 *   1. Free gift with purchase — auto-adds the configured gift variant when the merchandise
 *      subtotal crosses the threshold and removes it when it drops back below. The gift shows as
 *      free via a Shopify AUTOMATIC DISCOUNT (100% off the gift product, minimum cart = the
 *      threshold) configured in the admin — that discount is the server-side guard; this script
 *      only manages the line. A manual removal is remembered (session) so it isn't re-added on
 *      every tick; a drop below the threshold clears that so re-crossing re-adds it.
 *
 *   2. Cross-sell quick-add — delegated clicks on [data-cart-add] (the static cross-sell AND the
 *      dynamic complementary recommendations in sections/cart-recommendations).
 *
 *   3. Subscription upsell — delegated clicks on [data-sub-upsell]: adds the line on its Recharge
 *      selling plan and removes the one-time line (a plan change is an add+remove, not an edit).
 *
 *   4. Shipping-protection orphan reap — the toggle itself lives in
 *      assets/cart-shipping-protection.js; this file only pulls the protection line when the
 *      shopper empties the cart out from under it.
 *
 * Config is read from the JSON block #cart-upsell-config rendered by snippets/cart-drawer.liquid.
 */
(function () {
  const GIFT_PROP = '_free_gift';
  const DISMISSED_KEY = 'cyclone_gift_dismissed';
  const ADDED_KEY = 'cyclone_gift_added';
  const XSELL_OPEN_KEY = 'cyclone_cross_sell_open';
  let busy = false;

  function readConfig() {
    const el = document.getElementById('cart-upsell-config');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function session(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
  function setSession(key, val) {
    try {
      sessionStorage.setItem(key, val);
    } catch (e) {
      /* private mode — best effort */
    }
  }
  function clearSession(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      /* noop */
    }
  }

  async function getCart() {
    const res = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  function findGiftLine(cart) {
    return (cart.items || []).find((i) => i.properties && i.properties[GIFT_PROP]);
  }

  // Shipping-protection config (rendered into #cart-upsell-config by cart-drawer.liquid). Null unless
  // the feature is enabled with a product chosen.
  function protectionCfg() {
    const cfg = readConfig();
    const sp = cfg && cfg.shippingProtection;
    if (!sp || !sp.enabled || !sp.variantId) return null;
    return sp;
  }
  // ONE identity for the protection line, matching every Liquid guard (`item.variant_id == sp_vid`).
  // Product-handle or `i.id` matching would flag lines Liquid still renders as ordinary rows.
  function isProtectionLine(i, sp) {
    return !!sp && i.variant_id === sp.variantId;
  }

  // Merchandise subtotal in cents, EXCLUDING the gift line AND the shipping-protection line, so
  // eligibility matches what the shopper is really buying (protection is an add-on, not merchandise).
  function merchandiseSubtotal(cart, sp) {
    return (cart.items || []).reduce((sum, i) => {
      if (i.properties && i.properties[GIFT_PROP]) return sum;
      if (isProtectionLine(i, sp)) return sum;
      return sum + (i.final_line_price || 0);
    }, 0);
  }

  // Re-render the drawer + cart bubble from the Sections Rendering API (mirrors cart-drawer.js).
  async function rerenderCart() {
    try {
      const res = await fetch(`${window.routes.cart_url}?sections=cart-drawer,cart-icon-bubble`);
      const data = await res.json();
      if (data['cart-drawer']) {
        const parsed = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
        const fresh = parsed.querySelector('#CartDrawer');
        const target = document.querySelector('#CartDrawer');
        if (fresh && target) target.innerHTML = fresh.innerHTML;
      }
      if (data['cart-icon-bubble']) {
        const parsed = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
        const fresh = parsed.querySelector('.shopify-section');
        const target = document.querySelector('#cart-icon-bubble');
        if (fresh && target) target.innerHTML = fresh.innerHTML;
      }
      applyCollapseState();
    } catch (e) {
      /* re-render is best-effort; the next cart action will refresh it anyway */
    }
  }

  // Cross-sell collapse persistence. The drawer re-renders via the Sections API on every cart
  // mutation, which resets <details open> to the Liquid default (open when the cart has fewer than
  // the configured threshold of line items). Once the shopper manually expands or collapses
  // "Complete your order", remember that for the session and re-apply it after each re-render so
  // their choice sticks instead of snapping back to the default.
  function applyCollapseState() {
    // While the "Your pods need a battery" prompt is up, keep the cross-sell collapsed no matter the
    // saved preference — the two upsells stacked open squeeze the cart line item off-screen on mobile.
    // Liquid already renders it collapsed; this also overrides a stored "open" pref after re-render.
    if (document.querySelector('.cart-lightning-device')) {
      document.querySelectorAll('details[data-cross-sell-collapse]').forEach((d) => {
        d.open = false;
      });
      return;
    }
    const pref = session(XSELL_OPEN_KEY); // '1' open | '0' closed | null → keep the Liquid default
    if (pref === null) return;
    document.querySelectorAll('details[data-cross-sell-collapse]').forEach((d) => {
      d.open = pref === '1';
    });
  }

  function onToggle(e) {
    const d = e.target.closest && e.target.closest('details[data-cross-sell-collapse]');
    if (!d) return;
    setSession(XSELL_OPEN_KEY, d.open ? '1' : '0');
  }

  async function addVariant(variantId, properties, sellingPlan, quantity) {
    const line = { id: variantId, quantity: quantity || 1 };
    if (properties) line.properties = properties;
    if (sellingPlan) line.selling_plan = sellingPlan;
    await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [line] })
    });
  }

  async function removeLine(key) {
    await fetch(`${window.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
  }

  // Change the selling plan on an existing cart line IN PLACE. Identifying the line by its key keeps
  // it in the same cart position — unlike add-new-plan-line + remove-old, which appends the item to
  // the bottom and reads as the cart reordering itself. Pass null/'' to drop back to one-time.
  async function changeLineSellingPlan(key, sellingPlan) {
    await fetch(`${window.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, selling_plan: sellingPlan })
    });
  }

  // The core free-gift reconciler. One add OR one remove per call, off the cart snapshot taken by
  // reconcile(). Guarded against re-entry.
  async function reconcileGift(cart, sp) {
    const cfg = readConfig();
    const gift = cfg && cfg.freeGift;
    if (!gift || !gift.enabled || !gift.variantId || !gift.thresholdCents) return;

    const giftLine = findGiftLine(cart);
    const eligible = merchandiseSubtotal(cart, sp) >= gift.thresholdCents;

    // Scope the dismiss/added markers to THIS cart's token so a dismissal can't leak across
    // carts in the same session (mirrors the Medusa drawer's per-cart key).
    const dismissedKey = `${DISMISSED_KEY}_${cart.token}`;
    const addedKey = `${ADDED_KEY}_${cart.token}`;

    if (eligible && !giftLine && session(dismissedKey) !== '1') {
      // Was it added before and then removed by the shopper? Respect that (don't re-add).
      if (session(addedKey) === '1') {
        setSession(dismissedKey, '1');
        return;
      }
      busy = true;
      try {
        await addVariant(gift.variantId, { [GIFT_PROP]: 'true' });
        setSession(addedKey, '1');
        await rerenderCart();
      } finally {
        busy = false;
      }
    } else if (!eligible && giftLine) {
      // Dropped below threshold — pull the gift. Clear the "added" marker so re-crossing re-adds it.
      busy = true;
      try {
        await removeLine(giftLine.key);
        clearSession(addedKey);
        await rerenderCart();
      } finally {
        busy = false;
      }
    }
  }

  // Remove an orphaned shipping-protection line: the shopper turned protection on, then removed every
  // real item, leaving protection (and maybe the gift) alone in the cart. Protection with nothing to
  // protect is pulled automatically.
  async function reconcileProtection(cart, sp) {
    if (!sp) return;
    const protLine = (cart.items || []).find((i) => isProtectionLine(i, sp));
    if (!protLine) return;
    const hasReal = (cart.items || []).some(
      (i) => !isProtectionLine(i, sp) && !(i.properties && i.properties[GIFT_PROP])
    );
    if (hasReal) return;
    busy = true;
    try {
      await removeLine(protLine.key);
      await rerenderCart();
    } finally {
      busy = false;
    }
  }

  // One reconcile pass per cart mutation, off a SINGLE cart fetch. Gift first, then protection —
  // sequential so they don't fight over the shared `busy` lock, and safe to share one snapshot
  // because reconcileGift only ever touches the gift line, which reconcileProtection ignores.
  async function reconcile() {
    if (busy) return;
    let cart;
    try {
      cart = await getCart();
    } catch (e) {
      return;
    }
    const sp = protectionCfg();
    await reconcileGift(cart, sp);
    await reconcileProtection(cart, sp);
  }

  // Delegated clicks (buttons are re-rendered on every cart update, so listen once on the document):
  //   [data-cart-add]   → cross-sell quick-add
  //   [data-sub-upsell] → convert a one-time line to its Recharge subscription plan
  async function onClick(e) {
    const subBtn = e.target.closest('[data-sub-upsell]');
    if (subBtn) {
      e.preventDefault();
      if (busy) return;
      // Convert at the product's default (first) plan. The shopper picks a different delivery
      // frequency afterwards via the cadence <select> that renders on the subscribed line.
      const sellingPlan = subBtn.getAttribute('data-selling-plan-id');
      const lineKey = subBtn.getAttribute('data-line-key');
      if (!lineKey || !sellingPlan) return;
      busy = true;
      try {
        // Change the plan on the existing line in place so it keeps its cart position.
        await changeLineSellingPlan(lineKey, sellingPlan);
        await rerenderCart();
      } finally {
        busy = false;
      }
      return;
    }

    const btn = e.target.closest('[data-cart-add]');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId || busy) return;
    busy = true;
    try {
      await addVariant(variantId, null);
      await rerenderCart();
    } finally {
      busy = false;
    }
    // Adding a cross-sell item may cross the free-gift threshold.
    reconcile();
  }

  // Delegated change on the subscribed-line frequency <select> ([data-sub-change]). Registered in
  // the CAPTURE phase so we can stopPropagation before Dawn's cart-drawer-items change listener
  // fires — otherwise it treats the plan id as a quantity and POSTs a 400 to /cart/change.
  async function onCadenceChange(e) {
    const sel = e.target.closest('[data-sub-change]');
    if (!sel) return;
    e.stopPropagation();
    if (busy) return;
    const group = sel.closest('.cart-sub-upsell-group');
    if (!group) return;
    const lineKey = group.getAttribute('data-line-key');
    const sellingPlan = sel.value;
    if (!lineKey || !sellingPlan) return;
    busy = true;
    try {
      // Swap cadence in place so the line keeps its cart position (see changeLineSellingPlan).
      await changeLineSellingPlan(lineKey, sellingPlan);
      await rerenderCart();
    } finally {
      busy = false;
    }
  }

  function init() {
    document.addEventListener('click', onClick);
    document.addEventListener('change', onCadenceChange, true);
    // Toggle doesn't bubble — listen in the capture phase so a document-level handler still sees the
    // shopper open/close the cross-sell, then persist it for the session.
    document.addEventListener('toggle', onToggle, true);
    // React to every cart mutation (add/qty/remove) published on Dawn's pub/sub bus.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () =>
        setTimeout(() => {
          reconcile();
          applyCollapseState();
        }, 50)
      );
    }
    // And once on load, in case the stored cart already qualifies / the shopper has a saved preference.
    reconcile();
    applyCollapseState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
